<bn-care-plan-editor-shell
  headingId="action-editor-dialog-title"
  [heading]="mode === 'add' ? 'Add action' : 'Edit action'"
>
  <div carePlanEditorTitle class="bn-action-editor__title-field">
    <label for="action-title" class="bn-action-editor__label">
      Action title <span aria-hidden="true">*</span>
    </label>
    <input
      id="action-title"
      class="bn-action-editor__title-control"
      type="text"
      maxlength="200"
      aria-describedby="action-title-count"
      [ngModel]="title()"
      (ngModelChange)="title.set($event)"
      placeholder="Type what needs to happen — e.g. Submit the assistance application"
    />
    <p id="action-title-count" class="text-sm text-surface-600 m-0">
      {{ title().length }} of 200 characters
    </p>
    @if (titleError(); as error) {
      <p class="bn-action-editor__error m-0" role="alert">{{ error }}</p>
    }
    <label for="action-description" class="bn-action-editor__label">
      Action description (optional)
    </label>
    <textarea
      id="action-description"
      class="bn-action-editor__description-control"
      rows="3"
      maxlength="500"
      placeholder="What it involves and why it fits this client"
      [ngModel]="description()"
      (ngModelChange)="description.set($event)"
      aria-describedby="action-description-error"
    ></textarea>
    @if (descriptionError(); as error) {
      <p
        id="action-description-error"
        class="bn-action-editor__error m-0"
        role="alert"
      >
        {{ error }}
      </p>
    }
  </div>

  <div carePlanEditorMetadata class="bn-action-editor__metadata-content">
    @if (mode === 'add' && caseOptions) {
      <bn-care-plan-editor-row
        icon="pi-folder"
        label="Case"
        inputId="action-case"
      >
        <p-select
          class="bn-action-editor__select"
          inputId="action-case"
          [options]="caseOptions"
          optionLabel="label"
          optionValue="value"
          appendTo="body"
          [fluid]="true"
          [ngModel]="selectedCaseId()"
          (ngModelChange)="selectedCaseId.set($event)"
        />
      </bn-care-plan-editor-row>
    }
    <bn-care-plan-editor-row
      icon="pi-users"
      label="Assigned to"
      inputId="action-assignee-type"
    >
      <p-select
        class="bn-action-editor__select"
        inputId="action-assignee-type"
        [options]="assigneeTypes"
        optionLabel="label"
        optionValue="value"
        optionDisabled="disabled"
        appendTo="body"
        [fluid]="true"
        [ngModel]="assigneeType()"
        (ngModelChange)="assigneeType.set($event)"
      />
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-user"
      label="FA name"
      inputId="action-owner"
      [required]="true"
    >
      <div class="bn-action-editor__owner">
        <p-autocomplete
          styleClass="bn-action-editor__assignee"
          panelStyleClass="bn-action-editor__assignee-panel"
          inputId="action-owner"
          optionLabel="displayName"
          dataKey="id"
          appendTo="body"
          [fluid]="true"
          [minLength]="0"
          [delay]="0"
          [completeOnFocus]="true"
          [dropdown]="true"
          [forceSelection]="true"
          [suggestions]="ownerSuggestions()"
          [ngModel]="ownerInput()"
          (ngModelChange)="changeOwner($event)"
          (completeMethod)="searchOwners($event.query)"
          [disabled]="saving() || !!savedWithAssignmentIssue()"
          placeholder="Search by name or email"
          [emptyMessage]="ownerSearchLoading() ? '' : 'No matching employees'"
        >
          <ng-template #empty>
            @if (ownerSearchLoading()) {
              <div
                role="status"
                aria-label="Searching employees"
                class="flex flex-col gap-2"
              >
                <p-skeleton width="12rem" height="1rem" />
                <p-skeleton width="9rem" height="1rem" />
              </div>
            } @else {
              No matching employees
            }
          </ng-template>
          <ng-template #item let-owner>
            <div class="bn-action-editor__owner-option">
              <span
                >{{ owner.displayName
                }}{{ owner.isCurrentUser ? ' (Assign to me)' : '' }}</span
              >
              @if (owner.email) {
                <small>{{ owner.email }}</small>
              }
            </div>
          </ng-template>
        </p-autocomplete>
        <small class="bn-action-editor__owner-hint">
          Type at least 2 characters to search all employees.
        </small>
        @if (ownerSearchError(); as error) {
          <p class="bn-action-editor__meta-error m-0" role="status">
            {{ error }}
          </p>
        }
      </div>
      @if (ownerError(); as error) {
        <p
          carePlanEditorError
          class="bn-action-editor__meta-error"
          role="alert"
        >
          {{ error }}
        </p>
      }
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-align-left"
      label="Action type"
      inputId="action-type"
      [required]="true"
    >
      <p-select
        class="bn-action-editor__select"
        inputId="action-type"
        [options]="actionTypes()"
        optionLabel="label"
        optionValue="code"
        appendTo="body"
        [fluid]="true"
        [disabled]="actionTypes().length === 0"
        placeholder="Select an action type"
        [ngModel]="actionTypeCode()"
        (ngModelChange)="actionTypeCode.set($event)"
      />
      @if (typeError(); as error) {
        <p
          carePlanEditorError
          class="bn-action-editor__meta-error"
          role="alert"
        >
          {{ error }}
        </p>
      }
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-phone"
      label="Communication mode"
      inputId="action-communication"
    >
      <p-select
        class="bn-action-editor__select"
        inputId="action-communication"
        [options]="communicationPreferences()"
        optionLabel="label"
        optionValue="code"
        appendTo="body"
        [fluid]="true"
        [ngModel]="communicationPreferenceCode() ?? ''"
        (ngModelChange)="communicationPreferenceCode.set($event || null)"
      />
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-calendar"
      label="Due date"
      inputId="action-due-date"
      [required]="true"
    >
      <input
        id="action-due-date"
        class="bn-action-editor__date"
        type="date"
        [attr.min]="mode === 'add' ? minimumDueDate : null"
        [ngModel]="dueDate()"
        (ngModelChange)="dueDate.set($event)"
      />
      @if (dueDateError(); as error) {
        <p
          carePlanEditorError
          class="bn-action-editor__meta-error"
          role="alert"
        >
          {{ error }}
        </p>
      }
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-flag"
      label="Priority"
      inputId="action-priority"
      [required]="true"
    >
      <p-select
        class="bn-action-editor__select"
        inputId="action-priority"
        [options]="priorities()"
        optionLabel="label"
        optionValue="code"
        appendTo="body"
        [fluid]="true"
        [ngModel]="priorityCode()"
        (ngModelChange)="priorityCode.set($event)"
      />
      @if (priorityError(); as error) {
        <p
          carePlanEditorError
          class="bn-action-editor__meta-error"
          role="alert"
        >
          {{ error }}
        </p>
      }
    </bn-care-plan-editor-row>

    <bn-care-plan-editor-row
      icon="pi-check-circle"
      label="Status"
      inputId="action-status"
    >
      @if (mode === 'edit') {
        <p-select
          class="bn-action-editor__select"
          inputId="action-status"
          [options]="statuses()"
          optionLabel="label"
          optionValue="code"
          appendTo="body"
          [fluid]="true"
          [disabled]="statuses().length === 0"
          ariaLabel="Action status"
          [ngModel]="statusCode()"
          (ngModelChange)="statusCode.set($event)"
        />
      } @else {
        <span id="action-status" class="bn-action-editor__status-value">{{
          statusLabel()
        }}</span>
      }
    </bn-care-plan-editor-row>
  </div>

  <div class="bn-action-editor__promise">
    <p-checkbox
      inputId="action-client-promise"
      [binary]="true"
      [ngModel]="clientPromise()"
      (ngModelChange)="clientPromise.set($event)"
    />
    <label for="action-client-promise">
      <strong>Client promise</strong>
      <small>Client is expecting follow-up on this date.</small>
    </label>
  </div>

  @if (mode === 'edit') {
    <div class="bn-action-editor__timestamps">
      <span><strong>Created</strong>{{ createdLabel() }}</span>
      <span><strong>Last updated</strong>{{ updatedLabel() }}</span>
    </div>
  }

  @if (saveError(); as error) {
    <p class="bn-action-editor__save-error m-0" role="alert">{{ error }}</p>
  }

  <div carePlanEditorFooter class="bn-action-editor__footer-content">
    <button
      type="button"
      class="bn-action-editor__cancel"
      [disabled]="saving()"
      (click)="tryClose()"
    >
      {{ savedWithAssignmentIssue() ? 'Close' : 'Cancel' }}
    </button>
    <p-button
      [rounded]="true"
      label="Save"
      [loading]="saving()"
      [disabled]="saving() || !!savedWithAssignmentIssue()"
      (onClick)="submit()"
    />
  </div>
</bn-care-plan-editor-shell>

<p-confirmDialog />
